/**
 * 📦 COMPOSANT : sellerie-app/frontend/src\pages\Clients\ClientDetails.tsx
 * Ce composant est utilisé dans d'autres pages de l'application
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { clientService, Client } from '../../services/ClientService';
import { devisApiService } from '../../services/DevisApiService';

interface Devis {
  id: string;
  reference: string;
  date: string;
  montant: number;
  statut: 'en_attente' | 'accepte' | 'refuse';
}

const ClientDetails: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [client, setClient] = useState<Client | null>(null);
  const [devis, setDevis] = useState<Devis[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<string>('');

  // Charger les données du client depuis le service
  useEffect(() => {
    const fetchClient = async () => {
      try {
        setLoading(true);
        
        // Vérifier si l'ID est défini
        if (!id) {
          setError("ID du client non spécifié");
          setLoading(false);
          return;
        }
        
        // Récupérer le client depuis le service
        const clientData = await clientService.getClient(id!);
        setClient(clientData);
        
        // Récupérer les devis du client
        const allDevis = await devisApiService.getDevis();
        const clientDevis = allDevis.filter(d => d.client === id);
        
        setDevis(clientDevis.map(d => ({
          id: d.id || '',
          reference: d.reference || '',
          date: d.date || '',
          montant: d.montantTotal || 0,
          statut: 'en_attente' as 'en_attente' | 'accepte' | 'refuse'
        })));
        
        // Charger les notes du client
        setNotes(clientData.notes || '');
        
        setLoading(false);
      } catch (error) {
        console.error('Erreur lors du chargement du client:', error);
        setError('Client non trouvé');
        setLoading(false);
      }
    };
    
    fetchClient();
  }, [id]);

  // Gérer la création d'un nouveau devis pour ce client
  const creerDevis = () => {
    if (id) {
      navigate('/devis/nouveau', { state: { clientId: id } });
    }
  };

  // Gérer la modification du client
  const modifierClient = () => {
    // Rediriger vers la page de modification du client
    navigate(`/clients/${id}/edit`);
  };

  // Gérer la suppression du client
  const supprimerClient = async () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer le client "${client?.societe || `${client?.prenom} ${client?.nom}`}" ?`)) {
      try {
        if (!id) return;
        
        // Supprimer le client via le service
        await clientService.deleteClient(id);
        
        // Rediriger vers la liste des clients
        navigate('/clients');
      } catch (error) {
        console.error('Erreur lors de la suppression du client:', error);
        alert('Une erreur est survenue lors de la suppression du client');
      }
    }
  };

  // Gérer la sauvegarde des notes
  const sauvegarderNotes = async () => {
    try {
      if (!client) return;
      
      // Mettre à jour les notes du client
      const updatedClient = {
        ...client,
        notes
      };
      
      // Sauvegarder le client via le service
      await clientService.saveClient(updatedClient);
      
      alert('Notes sauvegardées avec succès');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des notes:', error);
      alert('Une erreur est survenue lors de la sauvegarde des notes');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement des données du client...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
        {error}
      </div>
    );
  }

  if (!client) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">
          {client.societe ? client.societe : `${client.prenom} ${client.nom}`}
        </h1>
        <div className="flex space-x-2">
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate('/clients')}
          >
            Retour
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={creerDevis}
          >
            Créer un devis
          </button>
          <button
            type="button"
            className="btn-outline"
            onClick={modifierClient}
          >
            Modifier
          </button>
          <button
            type="button"
            className="btn-danger"
            onClick={supprimerClient}
          >
            Supprimer
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Informations de contact</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm font-medium text-gray-500">Nom</h3>
            <p className="mt-1 text-sm text-gray-900">{client.prenom} {client.nom}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Email</h3>
            <p className="mt-1 text-sm text-gray-900">{client.email}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Téléphone</h3>
            <p className="mt-1 text-sm text-gray-900">{client.telephone}</p>
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-500">Adresse</h3>
            <p className="mt-1 text-sm text-gray-900">
              {client.adresse}<br />
              {client.codePostal} {client.ville}<br />
              {client.pays}
            </p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Historique des devis</h2>
        {devis.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucun devis pour ce client.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Référence
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {devis.map((devis) => (
                  <tr key={devis.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {devis.reference}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {devis.date}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {devis.montant.toLocaleString('fr-FR', {
                        style: 'currency',
                        currency: 'EUR',
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          devis.statut === 'accepte'
                            ? 'bg-green-100 text-green-800'
                            : devis.statut === 'refuse'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {devis.statut === 'accepte'
                          ? 'Accepté'
                          : devis.statut === 'refuse'
                          ? 'Refusé'
                          : 'En attente'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        className="text-primary-600 hover:text-primary-900 mr-3"
                        onClick={() => navigate(`/devis/${devis.id}`)}
                      >
                        Voir
                      </button>
                      <button
                        className="text-primary-600 hover:text-primary-900"
                        onClick={() => navigate(`/devis/${devis.id}/edit`)}
                      >
                        Modifier
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Notes et commentaires</h2>
          <button
            type="button"
            className="btn-primary"
            onClick={sauvegarderNotes}
          >
            Sauvegarder
          </button>
        </div>
        <textarea
          className="w-full h-32 p-2 border border-gray-300 rounded-md"
          value={notes}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
          placeholder="Ajouter des notes sur ce client..."
        ></textarea>
      </div>
    </div>
  );
};

export default ClientDetails;
